"use client";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import * as React from "react";

import { css, sva } from "@/../styled-system/css";
import ChevronDown from "@/assets/svg/chevron-down.svg";

const accordion = sva({
  slots: ["item", "header", "trigger", "content"],
  base: {
    item: { borderBottomWidth: "1px" },
    header: {
      display: "flex",
      width: "100%",
    },
    trigger: {
      display: "flex",
      flex: "1",
      alignItems: "center",
      justifyContent: "space-between",
      pt: "4",
      pb: "4",
      fontWeight: "semibold",
      lineHeight: "none",
      transition: "0.3s",
      userSelect: "none",
      cursor: "pointer",
      textAlign: "left",
      gap: "2",
      "& > svg": {
        flexShrink: "0",
        transition: "transform",
        transitionDuration: "normal",
      },
      "&[data-state=open] > svg": {
        transform: "rotate(180deg)",
      },
      _hover: {
        boxShadow: "0 2px 0 #41C8E6",
      },
    },
    content: {
      overflow: "hidden",
      transition: "all",
      animationFillMode: "forwards",
      '&[data-state="open"]': {
        animation: "accordionDown 0.2s ease-out",
      },
      '&[data-state="closed"]': {
        animation: "accordionUp 0.2s ease-out",
      },
    },
  },
});

const { item, header, trigger, content } = accordion.raw();

const Accordion = AccordionPrimitive.Root;

interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  strokeColor?: string;
  css?: object;
}

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & {
    css?: object;
  }
>(({ css: cssProp = {}, ...props }, ref) => {
  const className = css(item, cssProp);

  return <AccordionPrimitive.Item ref={ref} className={className} {...props} />;
});
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ children, strokeColor = "black", css: cssProp = {}, ...props }, ref) => {
  const className = css(trigger, cssProp);

  return (
    <AccordionPrimitive.Header className={css(header)}>
      <AccordionPrimitive.Trigger ref={ref} className={className} {...props}>
        {children}
        <ChevronDown stroke={strokeColor} width="1.5rem" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    css?: object;
  }
>(({ children, css: cssProp = {}, ...props }, ref) => {
  const className = css(content, cssProp);

  return (
    <AccordionPrimitive.Content ref={ref} className={className} {...props}>
      <div className={css({ p: "5" })}>{children}</div>
    </AccordionPrimitive.Content>
  );
});
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
